import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useMemo,
  useState
} from 'react';
import { API, graphqlOperation } from '@aws-amplify/api';
import { clientOrdersFiltered } from 'src/graphql/queries';
import type { ClientOrdersFilteredQuery } from 'src/API';

type ClientOrdersContextValue = {
  hasMultipleOrders: boolean | null;
  setHasMultipleOrders: (value: boolean) => void;
  refreshHasMultipleOrders: () => Promise<void>;
};

export const ClientOrdersContext = createContext<ClientOrdersContextValue>(
  {} as ClientOrdersContextValue
);

export function hasMultipleClientOrders(
  orders: Array<unknown> | null | undefined,
  nextToken?: string | null
): boolean {
  return (orders?.length ?? 0) > 1 || !!nextToken;
}

export const ClientOrdersProvider: FC<{ children: ReactNode }> = ({
  children
}) => {
  const [hasMultipleOrders, setHasMultipleOrders] = useState<boolean | null>(
    null
  );

  const refreshHasMultipleOrders = useCallback(async () => {
    try {
      const response = (await API.graphql(
        graphqlOperation(clientOrdersFiltered, { count: 2, reverse: true })
      )) as { data: ClientOrdersFilteredQuery };

      const paginated = response.data.clientOrdersFiltered;
      setHasMultipleOrders(
        hasMultipleClientOrders(paginated?.orders, paginated?.nextToken)
      );
    } catch (err) {
      console.error(err);
      setHasMultipleOrders(false);
    }
  }, []);

  const value = useMemo(
    () => ({
      hasMultipleOrders,
      setHasMultipleOrders,
      refreshHasMultipleOrders
    }),
    [hasMultipleOrders, refreshHasMultipleOrders]
  );

  return (
    <ClientOrdersContext.Provider value={value}>
      {children}
    </ClientOrdersContext.Provider>
  );
};
